import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Packages
import { Button, Input, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { FilterFilled, QuestionCircleFilled, SearchOutlined, SyncOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Components
import BooleanIcon from '../../utils/BooleanIcon';

// Actions
import { get_all_self_rules, self_rule_delete } from '../../../redux/actions/selfRulesActions';

// Utils
import { get_load_scope_desc, get_rule_action_desc, get_rule_type_desc } from '../../../utils/get_status_desc';

const RulesTable = (props) => {
	const { messageApi, orgFilters, setIsModalOpen, setUpdate, setRuleToUpdate, pageSize, skip, setSkip } = props;

	const { t } = useTranslation();

	const dispatch = useDispatch();

	const { all_self_rules_loading, all_self_rules, self_rule_delete_loading } = useSelector(state => state.self_rules);

	const searchInput = useRef(null);
	const [searchedColumn, setSearchedColumn] = useState('');
	const [searchText, setSearchText] = useState('');

	const update = (record) => {
		setIsModalOpen(true);
		setUpdate(true);
		setRuleToUpdate(record);
	}

	const deleteSelfRule = (record) => {
		const filters = { ...orgFilters, skip: skip, limit: pageSize };
		dispatch(self_rule_delete(messageApi, record._id.$oid, filters));
	}

	const handleSearch = (selectedKeys, confirm, dataIndex) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};
	
	const handleReset = (clearFilters) => {
		clearFilters();
		setSearchText('');
		setSearchedColumn('');
	};

	// Function to render compound rule conditions
	const renderCompoundConditions = (conditions, operator = 'AND', level = 0) => {
		if (!conditions || !Array.isArray(conditions)) return '';
		
		const conditionStrings = conditions.map((condition, index) => {
			if (condition.field_type === 5) {
				// Nested compound condition with its own operator
				const nestedOperator = condition.operator || 'AND';
				const nestedContent = renderCompoundConditions(condition.conditions, nestedOperator, level + 1);
				
				// Handle different operators
				if (condition.operator === 'NOT') {
					return `${t('statusDesc.ruleOperatorType.not')} (${nestedContent})`;
				} else if (condition.operator === 'OR' || condition.operator === 'AND') {
					// For OR/AND operators with single condition that has values,
					// still show the content but without extra parentheses
					// since the operator doesn't apply to anything (single condition)
					return nestedContent;
				}
				return nestedContent;
			} else if (condition.field_type === 3) {
				// Simple condition with values
				const values = condition.values ? condition.values.join(', ') : '';
				return values;
			}
			return '';
		}).filter(str => str);

		if (conditionStrings.length === 0) return '';
		if (conditionStrings.length === 1) return conditionStrings[0];
		
		// Use the operator parameter to join conditions at this level
		// Translate the operator for display
		const translatedOperator = operator === 'AND' ? t('statusDesc.ruleOperatorType.and') :
		                          operator === 'OR' ? t('statusDesc.ruleOperatorType.or') :
		                          operator === 'NOT' ? t('statusDesc.ruleOperatorType.not') :
		                          operator || t('statusDesc.ruleOperatorType.and');
		
		return conditionStrings.join(` ${translatedOperator} `);
	};

	const getColumnSearchProps = (dataIndex) => ({
		filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
			<div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
				<Input
					style={{ marginBottom: 8, display: 'block' }}
					ref={searchInput}
					placeholder={t('rulesForm.searchName')}
					value={selectedKeys[0]}
					onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
					onPressEnter={() => {
						handleSearch(selectedKeys, confirm, dataIndex);
						close();
					}}
				/>
				<Space>
					<Button
						style={{ width: 95 }}
						type='primary'
						size='small'
						icon={<SearchOutlined />}
						disabled={!selectedKeys[0]}
						onClick={() => {
							handleSearch(selectedKeys, confirm, dataIndex);
							close();
						}}
					>
						{t('locations.table.search.search')}
					</Button>
					<Button
						style={{ width: 110 }}
						size='small'
						icon={<SyncOutlined />}
						onClick={() => {
							clearFilters && handleReset(clearFilters);
							confirm();
							close();
						}}
					>
						{t('locations.table.search.reset')}
					</Button>
				</Space>
			</div>
		),
		filterIcon: () => (
			<SearchOutlined
				style={{
					color: searchText.length > 0 ? '#FFFFFF' : '#818D9C',
				}}
			/>
		),
		onFilter: (value, record) =>
			record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()),
		onFilterDropdownOpenChange: (visible) => {
			if (visible) {
				setTimeout(() => searchInput.current?.select(), 100);
			}
		},
		render: (text) => text
	});

	const columns = [
		...(orgFilters.organization === ''
			? [
					{
						title: t('filters.organization'),
						dataIndex: 'organization',
						key: 'organization',
						render: (organization) => (
							organization
								?	<Typography.Text copyable={{ text: organization?._id?.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
										{organization?.name}
									</Typography.Text>
							: '-'
						)
					}
				]
			: []
		),
		...(orgFilters.workspace === ''
			? [
					{
						title: t('filters.workspace'),
						dataIndex: 'workspace',
						key: 'workspace',
						render: (workspace) => (
							workspace
								?	<Typography.Text copyable={{ text: workspace?._id?.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
										{workspace?.name}
									</Typography.Text>
							: '-'
						)
					}
				]
			: []
		),
		...(orgFilters.module === ''
			? [
					{
						title: t('filters.module'),
						dataIndex: 'module',
						key: 'module',
						render: (module) => (
							module
								?	<Typography.Text copyable={{ text: module?._id?.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
										{module?.name}
									</Typography.Text>
							: '-'
						)
					}
				]
			: []
		),
		{
			title: t('routesTable.headers.name'),
			dataIndex: 'name',
			key: 'name',
			...getColumnSearchProps('name'),
			sorter: (a, b) => a.name.localeCompare(b.name),
			showSorterTooltip: false,
			render: (_, record) => (
				<Typography.Text copyable={{ text: record._id.$oid, tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')] }}>
					{record.name}
				</Typography.Text>
			)
		},
		// {
		// 	title: t('loadFileInput.description'),
		// 	dataIndex: 'description',
		// 	key: 'description'
		// },
		{
			title: t('schemasForm.inputs.scope'),
			dataIndex: 'scope',
			key: 'scope',
			width: 120,
			filters: [
				{
					text: t('statusDesc.loadScope.global'),
					value: 1,
				},
				{
					text: t('statusDesc.loadScope.organization'),
					value: 2,
				},
				{
					text: t('statusDesc.loadScope.workspace'),
					value: 3,
				},
				{
					text: t('statusDesc.loadScope.module'),
					value: 4,
				}
			],
			filterMode: 'tree',
			filterSearch: true,
			onFilter: (value, record) => record.scope === value,
			filterIcon: (filtered) => (
				<FilterFilled style={{ color: filtered ? 'white' : '#818D9C' }} />
			),
			render: (text) => (
				<Typography.Text>{get_load_scope_desc(text)}</Typography.Text>
			)
		},
		{
			title: t('rulesForm.inputs.active'),
			dataIndex: 'active',
			key: 'active',
			width: 100,
			filters: [
				{
					text: (
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<BooleanIcon value={true} /> <p className='ms-1 mb-0'>{t('schemasForm.booleanOptions.true')}</p>
						</div>
					),
					value: true,
				},
				{
					text: (
						<div style={{ display: 'flex', alignItems: 'center' }}>
							<BooleanIcon value={false} /> <p className='ms-1 mb-0'>{t('schemasForm.booleanOptions.false')}</p>
						</div>
					),
					value: false,
				}
			],
			filterMode: 'tree',
			filterSearch: true,
			onFilter: (value, record) => record.code === value,
			filterIcon: (filtered) => (
				<FilterFilled style={{ color: filtered ? 'white' : '#818D9C' }} />
			),
			render: (text) => (
				<BooleanIcon value={text} />
			)
		},
		{
			title: 'Index',
			dataIndex: 'index',
			key: 'index',
			width: 80
		},
		{
			title: t('schemas.title').slice(0, -1),
			dataIndex: ['reference', 'name'],
			key: 'reference'
		},
		{
			title: t('schemasForm.inputs.fieldType'),
			dataIndex: 'field_type',
			key: 'field_type',
			width: 150,
			filters: [
				{
					text: t('statusDesc.ruleFieldType.min'),
					value: 1,
				},
				{
					text: t('statusDesc.ruleFieldType.max'),
					value: 2,
				},
				{
					text: t('statusDesc.ruleFieldType.value'),
					value: 3,
				},
				{
					text: t('statusDesc.ruleFieldType.other'),
					value: 4,
				},
				{
					text: t('statusDesc.ruleFieldType.composite'),
					value: 5,
				}
			],
			filterMode: 'tree',
			filterSearch: true,
			onFilter: (value, record) => record.field_type === value,
			filterIcon: (filtered) => (
				<FilterFilled style={{ color: filtered ? 'white' : '#818D9C' }} />
			),
			render: (text) => (
				<Typography.Text>{get_rule_type_desc(text)}</Typography.Text>
			)
		},
		{
			title: t('rulesForm.inputs.values'),
			dataIndex: 'number',
			key: 'number',
			render: (text, record) => {
				if (record.field_type === 5) {
					// Compound rule - show conditions
					const conditionText = renderCompoundConditions(record.conditions, record.operator);
					return (
						<Typography.Paragraph style={{ display: 'block', margin: 0 }}>
							{conditionText || 'Complex Rule'}
						</Typography.Paragraph>
					);
				} else if (record.field_type >= 3) {
					// Simple rule with values
					return (
						<Typography.Paragraph style={{ display: 'block', margin: 0 }}>
							{record.values?.join(', ')}
						</Typography.Paragraph>
					);
				} else {
					// Numeric rules (min/max)
					return <Typography.Text>{text}</Typography.Text>;
				}
			}
		},
		{
			title: t('rulesForm.inputs.actionType'),
			dataIndex: 'action',
			key: 'action',
			width: 140,
			filters: [
				{
					text: t('statusDesc.ruleActionType.filter'),
					value: 'FILTER',
				},
				{
					text: t('statusDesc.ruleActionType.exclude'),
					value: 'EXCLUDE',
				},
				{
					text: t('statusDesc.ruleActionType.include'),
					value: 'INCLUDE',
				}
			],
			filterMode: 'tree',
			filterSearch: true,
			onFilter: (value, record) => record.action === value,
			filterIcon: (filtered) => (
				<FilterFilled style={{ color: filtered ? 'white' : '#818D9C' }} />
			),
			render: (text) => (
				<Typography.Text 
					style={{ 
						color: text === 'EXCLUDE' ? '#ff4d4f' : '#1677ff',
						fontWeight: 'bold'
					}}
				>
					{get_rule_action_desc(text)}
				</Typography.Text>
			)
		},
		{
			title: t('loadStatus.statusTable.date'),
			dataIndex: ['date', '$date'],
			key: 'date',
			width: 120,
			render: (text) => (
				<Typography.Text>{new Date(text).toLocaleString(localStorage.getItem('i18nextLng'), { hour12: true })}</Typography.Text>
			)
		},
		{
			title: t('routesTable.headers.action'),
			dataIndex: 'actions',
			key: 'actions',
			width: 120,
			render: (_, record) => (
				<Space>
					<Tooltip title={t('rulesForm.edit.title').split(' ')[0]}>
						<Button
							type='primary'
							shape='circle'
							size='large'
							icon={<i className='bi bi-pencil-fill'></i>}
							onClick={() => update(record)}
						/>
					</Tooltip>
					<Popconfirm
						title={t('rulesForm.delete.popconfirm.title')}
						description={t('rulesForm.delete.popconfirm.description')}
						icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
						onConfirm={() => deleteSelfRule(record)}
						okButtonProps={{
							loading: self_rule_delete_loading
						}}
						okText={t('rulesForm.delete.popconfirm.confirm')}
						cancelText={t('rulesForm.delete.popconfirm.cancel')}
					>
						<Button
							type='primary'
							shape='circle'
							size='large'
							danger
							icon={<span className='material-symbols-outlined'>delete</span>}
						/>
					</Popconfirm>
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pageSize;
		setSkip(skip);

		const rulessFilters = { ...orgFilters, skip: skip, limit: pageSize };
		dispatch(get_all_self_rules(rulessFilters));
	};

	return (
		<div className='mt-5'>
			<Spin spinning={all_self_rules_loading} tip={t('rules.loading')} size='large'>
				<Table
					columns={columns}
					dataSource={all_self_rules.rules}
					// onChange={handleTableChange}
					// pagination={{
					// 	pageSize: pageSize,
					// 	total: all_self_rules.count,
					// 	showTotal: (total, range) => `${range[0]} - ${range[1]} ${t('pagination.of')} ${total}`
					// }}
					pagination={false}
					scroll={{
						x: 1500,
						y: 79 * 10
					}}
				/>
			</Spin>
		</div>
	)
}

RulesTable.propTypes = {
	messageApi: PropTypes.object.isRequired,
	orgFilters: PropTypes.object.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	setUpdate: PropTypes.func.isRequired,
	setRuleToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default RulesTable;
